/**
 * 파일명 : evl0103m_slice.ts
 * 설명   : 인사평가 결과 집계 화면 Redux Slice
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0        2026-02-02    변재석    최초작성 (evl0103m.tsx에서 분리)
 ************************************************
 */

import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  IEvl0103MState,
  IEvaluationRecord,
  IInquiryCndsEvl0103M,
  IGradeStats,
  IScoreRangeStats,
} from '../../models/evl/evl0103m_model';

// ============================================================================
// 상수 정의
// ============================================================================

/**
 * 등급별 색상 매핑
 */
export const GRADE_COLORS: { [key: string]: string } = {
  S: 'purple',
  A: 'blue',
  B: 'green',
  C: 'orange',
  D: 'red',
};

/**
 * 등급별 점수 범위 및 목표 비율
 */
export const GRADE_RANGES: IGradeStats = {
  S: { min: 135, max: 150, color: '#722ed1', count: 0, targetPercent: 5 },
  A: { min: 120, max: 134, color: '#1890ff', count: 0, targetPercent: 20 },
  B: { min: 100, max: 119, color: '#52c41a', count: 0, targetPercent: 50 },
  C: { min: 80, max: 99, color: '#fa8c16', count: 0, targetPercent: 20 },
  D: { min: 0, max: 79, color: '#f5222d', count: 0, targetPercent: 5 },
};

/**
 * HR 등급 → 점수 변환 (서버에서도 동일 로직 사용)
 * S=1.0, A=0.9, B=0.8, C=0.7, D=0.6
 */
export const HR_GRADE_SCORE: { [key: string]: number } = {
  S: 1.0,
  A: 0.9,
  B: 0.8,
  C: 0.7,
  D: 0.6,
};

/**
 * PJ 등급 → 점수 변환 (서버에서도 동일 로직 사용)
 * A=1.0, B=0.75, C=0.5, D=0.25
 */
export const PJ_GRADE_SCORE: { [key: string]: number } = {
  A: 1.0,
  B: 0.75,
  C: 0.5,
  D: 0.25,
};

// ============================================================================
// 유틸리티 함수
// ============================================================================

/**
 * 점수로 등급 계산
 * @param score 2차평가 총점
 * @returns 등급 (S/A/B/C/D)
 */
export const getGradeFromScore = (score: number): string => {
  if (score >= 135) return 'S';
  if (score >= 120) return 'A';
  if (score >= 100) return 'B';
  if (score >= 80) return 'C';
  return 'D';
};

/**
 * 등급별 색상 반환
 * @param grade 등급
 * @returns 색상 문자열
 */
export const getGradeColor = (grade: string): string => {
  return GRADE_COLORS[grade] || 'default';
};

/**
 * 직급 근무기간 계산
 * @param currPosStDt 직급시작일자 (YYYYMMDD 형식)
 * @returns "X년 Y개월" 형식의 문자열
 */
export const calculatePositionTenure = (currPosStDt: string | null): string => {
  if (!currPosStDt) return '-';
  
  const year = parseInt(currPosStDt.substring(0, 4), 10);
  const month = parseInt(currPosStDt.substring(4, 6), 10);
  const day = parseInt(currPosStDt.substring(6, 8), 10);
  
  if (isNaN(year) || isNaN(month) || isNaN(day)) return '-';
  
  const startDate = new Date(year, month - 1, day);
  const today = new Date();
  
  let totalMonths = (today.getFullYear() - startDate.getFullYear()) * 12 
                    + (today.getMonth() - startDate.getMonth());
  
  if (today.getDate() < startDate.getDate()) {
    totalMonths--;
  }
  
  if (totalMonths < 0) totalMonths = 0;
  
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  
  if (years > 0 && months > 0) {
    return `${years}년 ${months}개월`;
  } else if (years > 0) {
    return `${years}년`;
  } else if (months > 0) {
    return `${months}개월`;
  } else {
    return '1개월 미만';
  }
};

/**
 * 등급별 통계 계산
 * @param evaluationData 평가 데이터 배열
 * @returns 등급별 통계
 */
export const calculateGradeStats = (evaluationData: IEvaluationRecord[]): IGradeStats => {
  const stats: IGradeStats = JSON.parse(JSON.stringify(GRADE_RANGES)); // deep copy
  
  evaluationData.forEach((record) => {
    const total = record.secondEvaluation.total;
    if (total >= 135) stats.S.count++;
    else if (total >= 120) stats.A.count++;
    else if (total >= 100) stats.B.count++;
    else if (total >= 80) stats.C.count++;
    else stats.D.count++;
  });
  
  return stats;
};

/**
 * 점수 구간별 초기값
 */
export const SCORE_RANGES: IScoreRangeStats = {
  above90: { label: '90점 이상', min: 90, max: 100, color: '#722ed1', gradient: 'linear-gradient(135deg, #722ed1, #9254de)', icon: '🏆', count: 0 },
  above80: { label: '80점 이상', min: 80, max: 89.99, color: '#1890ff', gradient: 'linear-gradient(135deg, #1890ff, #40a9ff)', icon: '⭐', count: 0 },
  above70: { label: '70점 이상', min: 70, max: 79.99, color: '#52c41a', gradient: 'linear-gradient(135deg, #52c41a, #73d13d)', icon: '✅', count: 0 },
  above60: { label: '60점 이상', min: 60, max: 69.99, color: '#fa8c16', gradient: 'linear-gradient(135deg, #fa8c16, #ffa940)', icon: '📋', count: 0 },
  notEvaluated: { label: '미평가', min: 0, max: 0, color: '#8c8c8c', gradient: 'linear-gradient(135deg, #8c8c8c, #bfbfbf)', icon: '⏳', count: 0 },
};

/**
 * 점수 구간별 통계 계산
 * @param evaluationData 평가 데이터 배열
 * @returns 점수 구간별 통계
 */
export const calculateScoreRangeStats = (evaluationData: IEvaluationRecord[]): IScoreRangeStats => {
  const stats: IScoreRangeStats = JSON.parse(JSON.stringify(SCORE_RANGES)); // deep copy
  
  evaluationData.forEach((record) => {
    // 미평가: 2차 제출여부가 'Y'가 아닌 사람
    if (record.eval2SubYn !== 'Y') {
      stats.notEvaluated.count++;
    } else {
      const score = record.finalScore || 0;
      if (score >= 90) stats.above90.count++;
      else if (score >= 80) stats.above80.count++;
      else if (score >= 70) stats.above70.count++;
      else stats.above60.count++;
    }
  });
  
  return stats;
};

/**
 * 점수 구간 키 반환
 * @param record 평가 레코드
 * @returns 점수 구간 키
 */
export const getScoreRangeKey = (record: IEvaluationRecord): string => {
  if (record.eval2SubYn !== 'Y') return 'notEvaluated';
  const score = record.finalScore || 0;
  if (score >= 90) return 'above90';
  if (score >= 80) return 'above80';
  if (score >= 70) return 'above70';
  return 'above60';
};

// ============================================================================
// 초기 상태
// ============================================================================

const initialState: IEvl0103MState = {
  inquiryCnds: {
    evlYear: '',
    empNo: '',
    evaluateeName: '',
    evaluatorName: '',
    position: '',
    department: '',
  },
  evaluationData: [],
  filteredData: [],
  evaluationYears: [],
  selectedGrades: [],
  loading: false,
  error: null,
};

// ============================================================================
// Slice 정의
// ============================================================================

export const evl0103m_Slice = createSlice({
  name: 'evl0103m_Slice',
  initialState: initialState,
  reducers: {
    // 상태 초기화
    initState: (state) => {
      Object.assign(state, { ...initialState });
    },
    // 조회조건 설정
    setInquiryCnds: (state, action: PayloadAction<Partial<IInquiryCndsEvl0103M>>) => {
      state.inquiryCnds = { ...state.inquiryCnds, ...action.payload };
    },
    // 평가 데이터 설정
    setEvaluationData: (state, action: PayloadAction<IEvaluationRecord[]>) => {
      state.evaluationData = action.payload;
      state.filteredData = action.payload;
    },
    // 필터링된 데이터 설정
    setFilteredData: (state, action: PayloadAction<IEvaluationRecord[]>) => {
      state.filteredData = action.payload;
    },
    // 평가년도 목록 설정
    setEvaluationYears: (state, action: PayloadAction<{ evlYear: string }[]>) => {
      state.evaluationYears = action.payload;
    },
    // 선택된 등급 설정
    setSelectedGrades: (state, action: PayloadAction<string[]>) => {
      state.selectedGrades = action.payload;
    },
    // 등급 토글
    toggleGrade: (state, action: PayloadAction<string>) => {
      const grade = action.payload;
      if (state.selectedGrades.includes(grade)) {
        state.selectedGrades = state.selectedGrades.filter(g => g !== grade);
      } else {
        state.selectedGrades.push(grade);
      }
    },
    // 로딩 상태 설정
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    // 에러 설정
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export default evl0103m_Slice.reducer;
export const Evl0103mAction = evl0103m_Slice.actions;
export const Evl0103mReducer = evl0103m_Slice.reducer;
